import React from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Grid,
	Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import moment from 'moment';
import { ShiftRequest } from 'types/shift-requests';
import { ReadonlyFormField } from 'core/components/ReadonlyFormField';
import { getTimeOffReasons } from 'core/API/scheduler';
import { formatTimeOffPeriod, isTimedTimeOff } from '../../utils/timeOffRequestForm';
import {
	formatTimeOffPersonName,
	getTimeOffApproverName,
} from '../../utils/timeOffCalendarActions';

interface TimeOffDetailDialogProps {
	open: boolean;
	request: ShiftRequest | null;
	container?: Element | (() => Element | null) | null;
	onClose: () => void;
}

const resolveReasonName = (reasonData: unknown, reasons: any[]): string => {
	if (!reasonData) return '';

	let reasonIri: string | null = null;
	if (typeof reasonData === 'object' && reasonData && '@id' in reasonData) {
		reasonIri = String((reasonData as { ['@id']?: string })['@id'] || '');
	} else if (typeof reasonData === 'string') {
		reasonIri = reasonData;
	}

	if (!reasonIri) return '';
	const reason = reasons.find((item: any) => item['@id'] === reasonIri);
	return reason?.name || '';
};

const TimeOffDetailDialog: React.FC<TimeOffDetailDialogProps> = ({
	open,
	request,
	container,
	onClose,
}) => {
	const { data: timeOffReasonsData = [] } = useQuery(
		['time-off-reasons'],
		() => getTimeOffReasons(),
		{ enabled: open }
	);

	if (!request) return null;

	const timed = isTimedTimeOff(request.startAt, request.endAt);
	const employeeName = formatTimeOffPersonName(request.employeeRequest) || 'Unknown Employee';
	const approverName = getTimeOffApproverName(request);
	const approvedAt = request.updated_at || (request as { updatedAt?: string }).updatedAt;
	const reasonName = resolveReasonName(
		(request as any).timeOffReasons || request.reason,
		timeOffReasonsData
	);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			container={container}
			aria-labelledby="time-off-detail-title"
			PaperProps={{
				sx: {
					borderRadius: 2,
					maxHeight: '90vh',
				},
			}}
		>
			<DialogContent sx={{ pt: 3, pb: 1 }}>
				<Typography id="time-off-detail-title" variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
					Time Off Details
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<ReadonlyFormField label="Employee" value={employeeName} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<ReadonlyFormField label="Status" value="Approved" />
					</Grid>
					<Grid item xs={12} sm={6}>
						<ReadonlyFormField
							label="Approved By"
							value={approverName || 'Not specified'}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<ReadonlyFormField
							label="Approved On"
							value={approvedAt ? moment(approvedAt).format('MMM D, YYYY h:mm A') : ''}
						/>
					</Grid>
					<Grid item xs={12}>
						<ReadonlyFormField
							label="Time Off Period"
							value={formatTimeOffPeriod(request.startAt, request.endAt)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<ReadonlyFormField
							label="Start Date"
							value={request.startAt ? moment(request.startAt).format('YYYY-MM-DD') : ''}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<ReadonlyFormField
							label="End Date"
							value={request.endAt ? moment(request.endAt).format('YYYY-MM-DD') : ''}
						/>
					</Grid>
					{timed && (
						<>
							<Grid item xs={12} sm={6}>
								<ReadonlyFormField
									label="Start Time"
									value={request.startAt ? moment(request.startAt).format('h:mm A') : ''}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ReadonlyFormField
									label="End Time"
									value={request.endAt ? moment(request.endAt).format('h:mm A') : ''}
								/>
							</Grid>
						</>
					)}
					<Grid item xs={12}>
						<ReadonlyFormField label="Reason" value={reasonName} />
					</Grid>
					{request.reasonDescription && (
						<Grid item xs={12}>
							<ReadonlyFormField
								label="Reason Description"
								value={request.reasonDescription}
								multiline
								minRows={2}
							/>
						</Grid>
					)}
					{request.note && (
						<Grid item xs={12}>
							<ReadonlyFormField label="Notes" value={request.note} multiline minRows={2} />
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Box sx={{ flex: 1 }} />
				<Button
					onClick={onClose}
					sx={{
						backgroundColor: 'rgb(68, 99, 136)',
						color: '#fff',
						textTransform: 'none',
						fontWeight: 500,
						px: 3,
						'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
					}}
				>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TimeOffDetailDialog;
